// API Keys management page
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '../../hooks/useTranslation'
import { KeyIcon, PlusIcon, TrashIcon, ClipboardDocumentIcon, CheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { EyeIcon as EyeIconSolid } from '@heroicons/react/20/solid'

interface ApiKey {
  id: string
  name: string
  keyPrefix: string
  scopes: string[]
  lastUsedAt: string | null
  lastUsedIp: string | null
  expiresAt: string | null
  createdAt: string
  isRevoked: boolean
}

interface CreateApiKeyResponse {
  id: string
  name: string
  keyPrefix: string
  key: string // Only returned on creation
  scopes: string[]
  expiresAt: string | null
  createdAt: string
  message: string
}

// API functions
async function fetchApiKeys(): Promise<{ apiKeys: ApiKey[] }> {
  const res = await fetch('/api/api-keys', { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch API keys')
  return res.json()
}

async function createApiKey(data: { name: string; scopes: string[]; expiresInDays?: number }): Promise<CreateApiKeyResponse> {
  const res = await fetch('/api/api-keys', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Failed to create API key')
  }
  return res.json()
}

async function revokeApiKey(id: string): Promise<void> {
  const res = await fetch(`/api/api-keys/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Failed to revoke API key')
  }
}

export function ApiKeysPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // State
  const [isCreating, setIsCreating] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyExpiry, setNewKeyExpiry] = useState<number | undefined>(undefined)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copiedKey, setCopiedKey] = useState(false)
  const [showKey, setShowKey] = useState(false)
  const [showRevokedKeys, setShowRevokedKeys] = useState(false)

  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: fetchApiKeys,
  })

  const createMutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      setCreatedKey(response.key)
      setNewKeyName('')
      setNewKeyExpiry(undefined)
      setIsCreating(false)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  // Handlers
  const handleCreate = () => {
    if (!newKeyName.trim()) return
    createMutation.mutate({
      name: newKeyName.trim(),
      scopes: ['card-scan', 'events-read'],
      expiresInDays: newKeyExpiry,
    })
  }

  const handleCopyKey = async () => {
    if (!createdKey) return
    await navigator.clipboard.writeText(createdKey)
    setCopiedKey(true)
    setTimeout(() => setCopiedKey(false), 2000)
  }

  const handleRevoke = (id: string, name: string) => {
    if (confirm(t('pages.apiKeys.revokeConfirm', { name }))) {
      revokeMutation.mutate(id)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const activeKeys = data?.apiKeys.filter(k => !k.isRevoked) || []
  const revokedKeys = data?.apiKeys.filter(k => k.isRevoked) || []

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and info */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.apiKeys.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.apiKeys.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsCreating(!isCreating)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {isCreating ? t('common.cancel') : t('pages.apiKeys.createButton')}
          </button>

          {/* Create Form */}
          {isCreating && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.apiKeys.createTitle')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('pages.apiKeys.fields.name')}
                  </label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder={t('pages.apiKeys.fields.namePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('pages.apiKeys.fields.expiration')}
                  </label>
                  <select
                    value={newKeyExpiry || ''}
                    onChange={(e) => setNewKeyExpiry(e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">{t('pages.apiKeys.fields.neverExpires')}</option>
                    <option value="30">{t('pages.apiKeys.fields.days30')}</option>
                    <option value="90">{t('pages.apiKeys.fields.days90')}</option>
                    <option value="180">{t('pages.apiKeys.fields.days180')}</option>
                    <option value="365">{t('pages.apiKeys.fields.year1')}</option>
                  </select>
                </div>
                <button
                  onClick={handleCreate}
                  disabled={!newKeyName.trim() || createMutation.isPending}
                  className="w-full px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? t('pages.apiKeys.creating') : t('pages.apiKeys.createKey')}
                </button>
                {createMutation.isError && (
                  <p className="text-sm text-red-600">{createMutation.error.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.apiKeys.stats' as any)}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.apiKeys.activeKeys')}</dt>
                <dd className="font-medium text-gray-900">{activeKeys.length}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.apiKeys.revokedKeys')}</dt>
                <dd className="font-medium text-gray-900">{revokedKeys.length}</dd>
              </div>
            </dl>
          </div>

          {/* Usage Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.apiKeys.howToUse')}</h3>
            <p className="text-sm text-gray-500 mb-2">
              {t('pages.apiKeys.howToUseDescription')}
            </p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto text-gray-600">
{`curl -H "X-API-Key: jsk_xxx..." \\
  https://api.com/mobile/scan`}
            </pre>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.apiKeys.title')}</h1>
            <button
              type="button"
              onClick={() => setIsCreating(!isCreating)}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {isCreating ? t('common.cancel') : t('pages.apiKeys.createButton')}
            </button>
            {isCreating && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.apiKeys.createTitle')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('pages.apiKeys.fields.name')}
                    </label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={t('pages.apiKeys.fields.namePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('pages.apiKeys.fields.expiration')}
                    </label>
                    <select
                      value={newKeyExpiry || ''}
                      onChange={(e) => setNewKeyExpiry(e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="">{t('pages.apiKeys.fields.neverExpires')}</option>
                      <option value="30">{t('pages.apiKeys.fields.days30')}</option>
                      <option value="90">{t('pages.apiKeys.fields.days90')}</option>
                      <option value="180">{t('pages.apiKeys.fields.days180')}</option>
                      <option value="365">{t('pages.apiKeys.fields.year1')}</option>
                    </select>
                  </div>
                  <button
                    onClick={handleCreate}
                    disabled={!newKeyName.trim() || createMutation.isPending}
                    className="w-full px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? t('pages.apiKeys.creating') : t('pages.apiKeys.createKey')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Created Key Banner */}
          {createdKey && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-green-800">{t('pages.apiKeys.keyCreated')}</h3>
                  <p className="text-sm text-green-700 mt-1">
                    {t('pages.apiKeys.keyCreatedWarning')}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white border border-green-300 rounded font-mono text-sm">
                      {showKey ? createdKey : createdKey.substring(0, 12) + '•'.repeat(32)}
                    </code>
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="p-2 text-green-600 hover:bg-green-100 rounded"
                      title={showKey ? t('pages.apiKeys.hide') : t('pages.apiKeys.show')}
                    >
                      {showKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={handleCopyKey}
                      className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      {copiedKey ? (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          {t('pages.apiKeys.copied')}
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          {t('pages.apiKeys.copy')}
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setCreatedKey(null)
                      setShowKey(false)
                    }}
                    className="mt-3 text-sm text-green-700 hover:underline"
                  >
                    {t('pages.apiKeys.dismiss')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.apiKeys.fields.name')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.apiKeys.fields.keyPrefix')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.apiKeys.fields.lastUsed')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.apiKeys.fields.expires')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.apiKeys.fields.created')}</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activeKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 text-sm">
                      <div className="font-medium text-gray-900">{key.name}</div>
                      <div className="text-xs text-gray-500">{key.scopes.join(', ')}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {key.keyPrefix}...
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {key.lastUsedAt ? (
                        <div>
                          <div>{formatDate(key.lastUsedAt)}</div>
                          {key.lastUsedIp && (
                            <div className="text-xs text-gray-400">{key.lastUsedIp}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">{t('pages.apiKeys.fields.never')}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {key.expiresAt ? formatDate(key.expiresAt) : t('pages.apiKeys.fields.never')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatDate(key.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <button
                        onClick={() => handleRevoke(key.id, key.name)}
                        disabled={revokeMutation.isPending}
                        className="text-red-600 hover:text-red-900"
                        title={t('pages.apiKeys.revokeButton')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {activeKeys.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                      {t('pages.apiKeys.noActiveKeys')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Revoked Keys */}
          {revokedKeys.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowRevokedKeys(!showRevokedKeys)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <EyeIconSolid className="h-4 w-4" />
                {showRevokedKeys ? t('pages.apiKeys.hideRevoked' as any) : t('pages.apiKeys.showRevoked' as any)} ({revokedKeys.length})
              </button>

              {showRevokedKeys && (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-500">{t('pages.apiKeys.fields.name')}</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-500">{t('pages.apiKeys.fields.keyPrefix')}</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-500">{t('pages.apiKeys.fields.created')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {revokedKeys.map((key) => (
                        <tr key={key.id} className="bg-gray-50 opacity-60">
                          <td className="px-3 py-4 text-sm">
                            <div className="font-medium text-gray-500 line-through">{key.name}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <code className="text-sm text-gray-400 bg-gray-100 px-2 py-1 rounded">
                              {key.keyPrefix}...
                            </code>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-400">
                            {formatDate(key.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
